import React, { useState, useRef, useEffect } from "react";
import { AudioVisualizer } from "react-audio-visualize";
import Icon from "../Icon";
import { toast } from "react-toastify";
import Button from "../Button";

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
};

const AudioPlayer = ({ audioBlob }: { audioBlob: Blob }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const visualizerRef = useRef<HTMLDivElement | null>(null);
  const progressRef = useRef<HTMLDivElement | null>(null);
  const [audioUrl, setAudioUrl] = useState("");

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !visualizerRef.current) return;
    
    const rect = visualizerRef.current.getBoundingClientRect();
    const seekPercent = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    const seekTime = seekPercent * duration;
    
    audioRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const togglePlay = async () => {
    if (!audioRef.current) return;

    try {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        if (window.AudioContext) {
          const ctx = new AudioContext();
          if (ctx.state === "suspended") await ctx.resume();
        }
        await audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    } catch (err:any) {
      console.error("Playback error:", err);
      toast.error(err)
    }
  };

  // Create blob URL and clean up
  useEffect(() => {
    const url = URL.createObjectURL(audioBlob);
    setAudioUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [audioBlob]);

  // Set up audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration || 0);

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", updateDuration);
    audio.addEventListener("ended", () => setIsPlaying(false));

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", updateDuration);
      audio.removeEventListener("ended", () => setIsPlaying(false));
    };
  }, [audioUrl]);

  return (
    <div className="flex items-center gap-2 rounded-lg align-middle text-white w-full max-w-md">
      {/* Play/Pause Button */}
      <Button
        onClick={togglePlay}
        className="p-2 bg-white text-cyan-600 rounded-full shrink-0 hover:scale-105 transition-transform mb-5"
        icon={isPlaying ? "pause" : "play"}
        color="info"
        iconSize="30px"
      />

      {/* Audio Visualizer and Progress Bar */}
      <div className="flex-grow flex flex-col gap-1">
        <div
          ref={visualizerRef}
          onClick={handleSeek}
          className="cursor-pointer relative group"
        >
          <AudioVisualizer
            blob={audioBlob}
            currentTime={currentTime}
            width={250}
            height={40}
            barWidth={2}
            gap={1}
            barColor="rgba(255,255,240,0.8)"
            barPlayedColor="rgb(8 145 178)"
          />


          {/* Playhead indicator */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-white opacity-0 group-hover:opacity-100 transition-all"
            style={{
              left: `${duration ? (currentTime / duration) * 100 : 0}%`,
            }}
          />
        </div>

        {/* Time Display */}
        <div className="flex justify-between text-xs mt-1">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>


      {/* Hidden Audio Element */}
      <audio ref={audioRef} src={audioUrl} preload="metadata" />
    </div>
  );
};

export default AudioPlayer;